import type {TemplateNodeDefinition} from "#template/template-node/types"
import {getTemplateNodeReturns} from "#template/template-node/validation"
import type {Version} from "@cm/graph-v3"
import {versionChain} from "@cm/graph-v3"
import {registerTemplateGraphNode} from "#template/template-node/register"
import {z} from "zod/v4-mini"
import {ImageNode} from "#template/nodes/image/image-node"
import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import type {NodeEvaluator} from "#template/node-evaluator"
import type {BuilderInlet, BuilderOutlet} from "#template/runtime-graph/graph-builder"
import type {ImageGenerator, ImageNodeGenerator} from "@cm/material-nodes"
import {materialConnection, TexImage} from "@cm/material-nodes"
import type {DataObjectWithRelated} from "@cm/utils"
import {hashObject} from "@cm/utils"
import {ImageColorSpace} from "@cm/utils/data-object"
import {FetchDataObject} from "#template/runtime-graph/nodes/fetch-data-object"

function colorSpaceForImageResource(imageResource: {main: {imageColorSpace?: ImageColorSpace | null}}): "Linear" | "sRGB" {
    return imageResource.main.imageColorSpace === ImageColorSpace.Linear ? "Linear" : "sRGB"
}

// the image node an uploaded image gives, which Blender's decals build again from the data object alone
export function dataObjectImageNode(dataObject: {id: string; legacyId: number; imageColorSpace?: ImageColorSpace | null}): ImageNodeGenerator {
    return {
        generator: ({uv, extension, interpolation, projection, disableProgressiveLoading, forceOriginalResolution}) => {
            const imageNode = new TexImage({
                parameters: {
                    inputs: {
                        vector: uv,
                    },
                    settings: {
                        extension,
                        interpolation,
                        projection,
                        disableProgressiveLoading,
                        forceOriginalResolution,
                        imageColorspaceSettingsName: colorSpaceForImageResource({main: dataObject}),
                        dataObjectId: dataObject.id,
                    },
                },
            })

            return {color: materialConnection(imageNode, "color"), alpha: materialConnection(imageNode, "alpha")}
        },
        hash: hashObject({
            type: "DataObjectReference",
            dataObjectId: dataObject.legacyId,
        }),
    }
}

export type DataObjectReferenceParameters = {
    name: string
    dataObjectId: number
}

type V0 = {
    dataObjectId: number
}

type V1 = V0 & {name: string}

const v0: Version<V0, V1> = {
    toNextVersion: (parameters) => {
        return {
            name: "Data Object Reference",
            ...parameters,
        }
    },
}

const dataObjectReferenceDefinition: TemplateNodeDefinition<DataObjectReferenceParameters> = {
    nodeClass: "DataObjectReference",
    validation: {
        parameters: z.object({
            name: z.string(),
            dataObjectId: z.number(),
        }),
        returns: getTemplateNodeReturns(),
    },
    versionChain: versionChain([v0]),
}

@registerTemplateGraphNode
export class DataObjectReference extends ImageNode<DataObjectReferenceParameters> {
    public override get definition() {
        return dataObjectReferenceDefinition
    }

    override evaluate(scope: GraphBuilderScope, evaluator: NodeEvaluator): BuilderInlet<ImageGenerator | null> {
        const dataObject = this.getDataObject(scope, evaluator)
        return scope.pureLambda<[DataObjectWithRelated, number], ImageGenerator>(
            scope.tuple(dataObject, this.parameters.dataObjectId),
            ([dataObject, dataObjectId]) => {
                return {
                    imageNode: dataObjectImageNode({...dataObject, legacyId: dataObjectId}),
                    metadata: {
                        width: dataObject.width ?? undefined,
                        height: dataObject.height ?? undefined,
                        legacyId: dataObject.legacyId,
                    },
                }
            },
            "dataObjectReference",
        )
    }

    getDataObject(scope: GraphBuilderScope, evaluator: NodeEvaluator): BuilderOutlet<DataObjectWithRelated> {
        const {templateContext} = evaluator
        return scope.node(FetchDataObject, {
            sceneManager: templateContext.sceneManager,
            dataObjectId: this.parameters.dataObjectId,
        }).dataObject
    }
}
